import type {
  CloudConnectionAccountDTO,
  CloudConnectionDTO,
} from '@deepguide-ai/types'

export type ConnectionScanTarget = {
  key: string
  provider: string
  connectionId?: string
  scanConnectionId: string
  label: string
}

type BuildConnectionScanTargetsInput = {
  provider: string
  connected: boolean
  accounts?: Array<
    Pick<
      CloudConnectionAccountDTO,
      'connectionId' | 'scanConnectionId' | 'label'
    >
  >
  connectionId?: string | null
  connectionLabel?: string
}

const FALLBACK_LABELS: Record<string, string> = {
  aws: 'AWS read-only connection',
  gcp: 'Google Cloud read-only connection',
  azure: 'Azure read-only connection',
}

export function hasActiveCloudResourceUsageDetection(
  connections: CloudConnectionDTO[] | undefined
): boolean {
  return (connections ?? []).some((connection) =>
    connection.accounts.some((account) =>
      ['waiting', 'active', 'delayed'].includes(
        account.resourceUsageDetectionState ?? ''
      )
    )
  )
}

export function connectionScanTargetKey(
  provider: string,
  connectionId?: string | null
): string {
  return connectionId ? `${provider}:${connectionId}` : provider
}

export function buildConnectionScanTargets({
  provider,
  connected,
  accounts,
  connectionId,
  connectionLabel,
}: BuildConnectionScanTargetsInput): ConnectionScanTarget[] {
  if (!connected) return []

  const targets =
    accounts && accounts.length > 0
      ? accounts
      : [
          {
            connectionId: connectionId ?? null,
            scanConnectionId: connectionId ?? 'default',
            label:
              connectionLabel ??
              FALLBACK_LABELS[provider] ??
              `${provider} connection`,
          },
        ]

  return targets.map((target) => ({
    key: connectionScanTargetKey(provider, target.connectionId),
    provider,
    connectionId: target.connectionId ?? undefined,
    scanConnectionId: target.scanConnectionId,
    label: target.label,
  }))
}

export type ConnectionScanDisplayState =
  | 'not_started'
  | 'starting'
  | 'running'
  | 'complete'
  | 'failed'
  | 'skipped'

export function deriveConnectionScanDisplayState(input: {
  runId?: string | null
  phase?: string | null
  isTerminal?: boolean
  isStarting?: boolean
  launchError?: string | null
}): ConnectionScanDisplayState {
  if (input.isStarting) return 'starting'
  if (input.launchError) return 'failed'
  if (!input.runId) return 'not_started'
  if (input.phase === 'failed') return 'failed'
  if (input.phase === 'skipped') return 'skipped'
  if (input.phase === 'complete') return 'complete'
  if (input.isTerminal) return 'failed'
  return 'running'
}
